import { z } from 'zod';
import { ClimateApiConfig } from '../services/climate/base/types';

/**
 * Environment configuration schema using Zod for validation
 */
const ClimateConfigSchema = z.object({
  CLIMATE_API_BASE_URL: z.string().min(1, 'Base URL cannot be empty'), // Allow relative paths for backend proxy
  CLIMATE_API_TIMEOUT: z.string().transform(val => parseInt(val, 10)).pipe(z.number().min(1000).max(300000)),
  CLIMATE_API_RETRY_ATTEMPTS: z.string().transform(val => parseInt(val, 10)).pipe(z.number().min(0).max(10)),
  CLIMATE_API_RETRY_DELAY: z.string().transform(val => parseInt(val, 10)).pipe(z.number().min(100).max(10000)),
  CLIMATE_API_ENABLE_LOGGING: z.string().transform(val => val === 'true'),
});

/**
 * Default configuration values
 */
const DEFAULT_CONFIG: ClimateApiConfig = {
  baseUrl: '/api/climate', // Use backend proxy instead of external API
  timeout: 30000, // 30 seconds
  retryAttempts: 3,
  retryDelay: 1000, // 1 second
  enableLogging: true
};

/**
 * Get environment variable safely
 */
function getEnvVar(key: string, defaultValue?: string): string | undefined {
  // Try to access environment variables safely
  try {
    const value = import.meta.env[key];
    return value || defaultValue;
  } catch {
    return defaultValue;
  }
}

/**
 * Check if we're in development mode
 */
function isDevelopment(): boolean {
  try {
    return import.meta.env.DEV === true || import.meta.env.MODE === 'development';
  } catch {
    return true; // Default to development if unable to determine
  }
}

/**
 * Get current environment mode
 */
function getEnvironmentMode(): string {
  try {
    return import.meta.env.MODE || 'development';
  } catch {
    return 'development';
  }
}

/**
 * Load and validate climate API configuration from environment variables
 */
function loadClimateConfig(): ClimateApiConfig {
  try {
    // Get environment variables with fallbacks
    const envConfig = {
      CLIMATE_API_BASE_URL: getEnvVar('CLIMATE_API_BASE_URL', DEFAULT_CONFIG.baseUrl),
      CLIMATE_API_TIMEOUT: getEnvVar('CLIMATE_API_TIMEOUT', DEFAULT_CONFIG.timeout.toString()),
      CLIMATE_API_RETRY_ATTEMPTS: getEnvVar('CLIMATE_API_RETRY_ATTEMPTS', DEFAULT_CONFIG.retryAttempts.toString()),
      CLIMATE_API_RETRY_DELAY: getEnvVar('CLIMATE_API_RETRY_DELAY', DEFAULT_CONFIG.retryDelay.toString()),
      CLIMATE_API_ENABLE_LOGGING: getEnvVar('CLIMATE_API_ENABLE_LOGGING', DEFAULT_CONFIG.enableLogging.toString()),
    };

    // Validate configuration
    const validatedConfig = ClimateConfigSchema.parse(envConfig);

    return {
      baseUrl: validatedConfig.CLIMATE_API_BASE_URL,
      timeout: validatedConfig.CLIMATE_API_TIMEOUT,
      retryAttempts: validatedConfig.CLIMATE_API_RETRY_ATTEMPTS,
      retryDelay: validatedConfig.CLIMATE_API_RETRY_DELAY,
      enableLogging: validatedConfig.CLIMATE_API_ENABLE_LOGGING,
    };
  } catch (error) {
    console.warn('Climate API configuration validation failed, using defaults:', error);
    
    // In development, log the error details
    if (isDevelopment()) {
      console.error('Climate config validation error details:', error);
    }
    
    return DEFAULT_CONFIG;
  }
}

/**
 * Get climate API configuration
 */
export function getClimateConfig(): ClimateApiConfig {
  return loadClimateConfig();
}

/**
 * Validate climate API configuration at startup
 */
export function validateClimateConfig(): void {
  const config = getClimateConfig();
  
  console.info('Climate API Configuration:', {
    baseUrl: config.baseUrl,
    timeout: config.timeout,
    retryAttempts: config.retryAttempts,
    retryDelay: config.retryDelay,
    enableLogging: config.enableLogging
  });

  // Validate base URL is reachable (optional health check)
  if (config.enableLogging) {
    console.info('Climate API will attempt to connect to:', config.baseUrl);
  }
}

/**
 * Get environment-specific configuration overrides
 */
export function getEnvironmentSpecificConfig(): Partial<ClimateApiConfig> {
  const env = getEnvironmentMode();
  
  switch (env) {
    case 'production':
      return {
        enableLogging: false,
        retryAttempts: 2,
        timeout: 25000
      };
    case 'staging':
      return {
        enableLogging: false,
        retryAttempts: 3,
        timeout: 30000
      };
    case 'development':
    case 'test':
    default:
      return {
        enableLogging: true,
        retryAttempts: 3,
        timeout: 30000
      };
  }
}

/**
 * Merge default config with environment-specific overrides
 */
export function getOptimizedClimateConfig(): ClimateApiConfig {
  const baseConfig = getClimateConfig();
  const envOverrides = getEnvironmentSpecificConfig();
  
  return {
    ...baseConfig,
    ...envOverrides
  };
}

// Export the default configuration for testing
export { DEFAULT_CONFIG }; 